
import { useState } from "react";
import { UserRole } from "@/hooks/useUserRoles";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { UserWithRoles } from "./types";
import { User } from "@supabase/supabase-js";

export const useRoleManagement = () => {
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithRoles | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState<UserRole>('admin');
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  
  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      // Fetch all users
      const { data: authUsers, error: authError } = await supabase.auth.admin.listUsers();
      
      if (authError) throw authError;
      
      // Check if we have the authorized admin user in the result
      const hasAuthorizedAdmin = authUsers.users.some(
        (user: User) => user.email === 'thiagogaravelo@gmail.com'
      );
      
      // For each user, fetch their roles
      const usersWithRoles = await Promise.all(
        authUsers.users.map(async (user: User) => {
          const { data: roles } = await supabase.rpc('get_user_roles', { _user_id: user.id });
          return {
            id: user.id,
            email: user.email || '',
            roles: roles || [],
          };
        })
      );
      
      if (!hasAuthorizedAdmin && currentUser?.email !== 'thiagogaravelo@gmail.com') {
        toast({
          title: "Aviso de Segurança",
          description: "Este sistema está configurado para acesso de produção exclusivo. Entre em contato com o administrador autorizado para obter acesso.",
          variant: "destructive",
          duration: 6000,
        });
      }
      
      setUsers(usersWithRoles);
    } catch (error) {
      console.error("Erro ao buscar usuários:", error);
      toast({
        title: "Erro",
        description: "Não foi possível buscar os usuários",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleAddRole = async () => {
    if (!selectedUser) return;
    
    try {
      // Check if current user is authorized
      if (currentUser?.email !== 'thiagogaravelo@gmail.com') {
        toast({
          title: "Acesso Negado",
          description: "Apenas o administrador autorizado pode modificar funções de usuário.",
          variant: "destructive",
        });
        setIsDialogOpen(false);
        return;
      }
      
      const { error } = await supabase
        .from('user_roles')
        .insert({
          user_id: selectedUser.id,
          role: newRole,
        });
        
      if (error) throw error;
      
      toast({
        title: "Sucesso",
        description: `Função ${newRole} adicionada para ${selectedUser.email}`,
      });
      
      // Update local state
      setUsers(users.map(user => {
        if (user.id === selectedUser.id) {
          return {
            ...user,
            roles: [...user.roles, newRole],
          };
        }
        return user;
      }));
      
      setIsDialogOpen(false);
    } catch (error: any) {
      console.error("Erro ao adicionar função:", error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível adicionar a função",
        variant: "destructive",
      });
    }
  };
  
  const handleRemoveRole = async (userId: string, roleToRemove: UserRole) => {
    try {
      // Check if current user is authorized
      if (currentUser?.email !== 'thiagogaravelo@gmail.com') {
        toast({
          title: "Acesso Negado",
          description: "Apenas o administrador autorizado pode modificar funções de usuário.",
          variant: "destructive",
        });
        return;
      }
      
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('role', roleToRemove);
        
      if (error) throw error;
      
      toast({
        title: "Sucesso",
        description: `Função ${roleToRemove} removida`,
      });
      
      // Update local state
      setUsers(users.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            roles: user.roles.filter(role => role !== roleToRemove),
          };
        }
        return user;
      }));
    } catch (error: any) {
      console.error("Erro ao remover função:", error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível remover a função",
        variant: "destructive",
      });
    }
  };

  const openAddRoleDialog = (user: UserWithRoles) => {
    // Check if current user is authorized before even showing the dialog
    if (currentUser?.email !== 'thiagogaravelo@gmail.com') {
      toast({
        title: "Acesso Negado",
        description: "Apenas o administrador autorizado pode adicionar funções de usuário.",
        variant: "destructive",
      });
      return;
    }
    
    setSelectedUser(user);
    setIsDialogOpen(true);
  };

  return {
    users,
    isLoading,
    fetchUsers,
    selectedUser,
    isDialogOpen,
    setIsDialogOpen,
    newRole,
    setNewRole,
    handleAddRole,
    handleRemoveRole,
    openAddRoleDialog
  };
};
